/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { App, Table, Tooltip } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { nanoid } from 'nanoid'
import { debounce } from 'lodash'
import {
  formatCurrency,
  getErrorMessage,
  isValidEmail,
} from '@sportninja/common/utils/utils'
import { useAdminTeamRegisterContext } from '../context'
import { SelectInput } from '../../ts-components/SelectInput'
import { TextInput } from '../../ts-components/TextInput'
import PhoneInput from '../../ts-components/PhoneInput'
import { SwitchInput } from '../../ts-components/SwitchInput'
import Button from '../../ts-components/Button'
import Text from '../../ts-components/Text'
import { req } from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'

interface TeamOfficialRecord {
  id?: string
  name: string
  email: string
  phone: string
  zipCode: string
  title: string
  country: string
  is_treasurer?: boolean
}

interface Team {
  id: string
  name: string
  name_full: string
  abbreviation: string | null
  is_public: boolean
  is_visible: boolean
  sport_id: string
  organization_id: string
  updated_at: string
  is_favorite: boolean
}

const useTeamSearch = (scheduleId: string) => {
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [query, setQuery] = useState('')
  const [isGlobal, setIsGlobal] = useState(false)

  const fetchTeams = useCallback(async () => {
    if (!scheduleId) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await req(
        `/registration/${scheduleId}/admin/search-not-registered?query=${query}&is_global=${
          isGlobal ? 1 : 0
        }`
      )
      setTeams(response.data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching teams')
      )
    } finally {
      setLoading(false)
    }
  }, [scheduleId, query, isGlobal])

  // Create a debounced version of fetchTeams
  const debouncedFetchTeams = useMemo(
    () => debounce(fetchTeams, 500),
    [fetchTeams]
  )

  // Effect to trigger search when query or isGlobal changes
  useEffect(() => {
    debouncedFetchTeams()
    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedFetchTeams.cancel()
    }
  }, [debouncedFetchTeams, query, isGlobal])

  const createTeam = useCallback(
    async ({
      name,
      sportId,
      organizationId,
    }: {
      name: string
      sportId: string
      organizationId: string
    }): Promise<Team> => {
      try {
        const response = await req('/teams', {
          method: 'POST',
          body: JSON.stringify({
            sport_id: sportId,
            name: name,
            schedule_id: scheduleId,
            organization_id: organizationId,
            is_public: true,
            is_visible: true,
          }),
        })
        return response.data
      } catch (err) {
        throw err instanceof Error
          ? err
          : new Error('An error occurred while creating team')
      }
    },
    [scheduleId]
  )

  return {
    teams,
    loading,
    error,
    query,
    setQuery,
    isGlobal,
    setIsGlobal,
    refetch: fetchTeams,
    createTeam,
  }
}

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  .form-row {
    display: flex;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .form-row-checkbox {
    display: flex;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .form-field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 1 0 0;
  }

  .form-field-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: stretch;
  }

  .section-separator {
    height: 1px;
    background-color: ${colors.SECONDARY_200};
    width: 100%;
  }
`

const AdminTeamRegisterForm = () => {
  const {
    state,
    setState,
    registration,
    messageApi,
    teamOfficialTypes,
    cost,
    deposit,
    singlePayment,
    countries,
    divisionId,
    costValue,
    setCustomErroByField,
    currency,
  } = useAdminTeamRegisterContext()

  const { width } = useWindowSize()
  const teamRef = useRef<any>(null)
  const isMobile = useMemo(() => width < 768, [width])
  const { message } = App.useApp()
  const [isCreatingTeam, setIsCreatingTeam] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [isAddingTeamOfficial, setIsAddingTeamOfficial] = useState(false)
  const [newTeamOfficial, setNewTeamOfficial] = useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    phone: '',
    country: '',
    zipCode: '',
    id: '',
    is_treasurer: false,
  })

  const { teams, createTeam, refetch, setQuery, setIsGlobal } = useTeamSearch(
    registration.schedule_id
  )

  const onSelect = useCallback((_value: string) => {
    teamRef.current?.blur()
  }, [])

  const validateCouponCode = useCallback(async () => {
    if (state?.couponCode === '') {
      return
    }
    try {
      const res = await req(`/registration/${divisionId}/coupon/verify`, {
        method: 'POST',
        body: JSON.stringify({
          code: state?.couponCode,
        }),
      })
      setState((prev) => ({ ...prev, coupon: res?.data }))
      message.open({
        type: 'success',
        content: `Coupon Validated Successfully: ${res?.data?.discount}${
          res?.data?.discount_type === 'percentage' ? '%' : ''
        } off`,
      })
    } catch (e: any) {
      message.open({
        key: 'coupon-error',
        type: 'error',
        content: e?.message,
      })
    }
  }, [state?.couponCode, divisionId, setState, message])

  const teamOptions = useMemo(
    () => [
      ...(teams?.map((team) => ({
        label: team.name,
        value: team.id,
      })) || []),
    ],
    [teams]
  )

  const titleOptions = useMemo(
    () =>
      teamOfficialTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
    [teamOfficialTypes]
  )

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        label: country.name,
        value: country.id,
      })),
    [countries]
  )

  const onCancel = useCallback(() => {
    setIsAddingTeamOfficial(false)
    setNewTeamOfficial({
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      phone: '',
      country: '',
      zipCode: '',
      id: '',
      is_treasurer: false,
    })
  }, [setIsAddingTeamOfficial, setNewTeamOfficial])

  const validateForm = useCallback(() => {
    // clear the errors
    setState((prev) => ({
      ...prev,
      fieldErrors: [],
      customError: {},
    }))

    if (newTeamOfficial.firstName === '') {
      setCustomErroByField('team_official_first_name', 'First Name is required')
      return false
    }
    if (newTeamOfficial.lastName === '') {
      setCustomErroByField('team_official_last_name', 'Last Name is required')
      return false
    }
    if (newTeamOfficial.email === '') {
      setCustomErroByField('team_official_email', 'Email is required')
      return false
    }
    if (!isValidEmail(newTeamOfficial.email)) {
      setCustomErroByField('team_official_email', 'Invalid email')
      return false
    }
    // if (newTeamOfficial.title === '') {
    //   setCustomErroByField('team_official_title', 'Title is required')
    //   return false
    // }
    // Phone Number Should be Optional
    // if (newTeamOfficial.phone === '') {
    //   setCustomErroByField('team_official_phone', 'Phone is required')
    //   return false
    // }
    // if (newTeamOfficial.country === '') {
    //   setCustomErroByField('team_official_country', 'Country is required')
    //   return false
    // }
    // if (newTeamOfficial.zipCode === '') {
    //   setCustomErroByField('team_official_zip_code', 'Zip Code is required')
    //   return false
    // }

    return true
  }, [newTeamOfficial, setState, setCustomErroByField])

  const onSave = useCallback(() => {
    // clear the errors
    setState((prev) => ({
      ...prev,
      fieldErrors: [],
      customError: {},
    }))
    // validate the form
    if (!validateForm()) {
      return
    }

    setState((prev) => ({
      ...prev,
      team_officials: newTeamOfficial.id
        ? prev.team_officials.map((official) => {
            if (official.id === newTeamOfficial.id) {
              return {
                ...newTeamOfficial,
                name: `${newTeamOfficial.firstName} ${newTeamOfficial.lastName}`,
              }
            }
            return official
          })
        : [
            ...prev.team_officials,
            {
              ...newTeamOfficial,
              id: nanoid(),
              name: `${newTeamOfficial.firstName} ${newTeamOfficial.lastName}`,
              is_treasurer: prev.team_officials.length === 0, // Set as treasurer if first official
            },
          ],
    }))
    setState((prev) => ({
      ...prev,
      fieldErrors: [],
      customError: '',
    }))
    setNewTeamOfficial({
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      phone: '',
      country: '',
      zipCode: '',
      id: '',
      is_treasurer: false,
    })
    setIsAddingTeamOfficial(false)
  }, [setState, validateForm, newTeamOfficial])

  const onSaveTeam = useCallback(async () => {
    try {
      const newTeam = await createTeam({
        name: newTeamName,
        sportId: registration.sport_id,
        organizationId: registration.organization_id,
      })
      await refetch()
      // Set the new team as selected
      setState((prev) => ({ ...prev, team: newTeam.id }))
      messageApi.open({
        type: 'success',
        content: 'Team Created Successfully',
      })
      const team = teams.find((t) => t.name === newTeamName)
      if (team) {
        setState((prev) => ({ ...prev, team: team.id }))
      }
      setIsCreatingTeam(false)
      setNewTeamName('')
    } catch (e) {
      messageApi.open({
        type: 'error',
        content: getErrorMessage(e),
      })
    }
  }, [
    createTeam,
    messageApi,
    newTeamName,
    refetch,
    registration.organization_id,
    registration.sport_id,
    setIsCreatingTeam,
    setState,
    teams,
  ])

  const onCancelTeam = useCallback(() => {
    setIsCreatingTeam(false)
    setNewTeamName('')
  }, [])

  const onEditTeamOfficial = (record: TeamOfficialRecord) => {
    setNewTeamOfficial({
      firstName: record?.name?.split(' ')[0],
      lastName: record?.name?.split(' ')[1],
      email: record?.email,
      title: record?.title,
      phone: record?.phone,
      country: record?.country,
      zipCode: record?.zipCode,
      id: record?.id || '',
      is_treasurer: record?.is_treasurer || false,
    })
    setIsAddingTeamOfficial(true)
  }

  return (
    <div css={formStyles}>
      <div
        css={css`
          display: flex;
          gap: 24px;
          width: 100%;
          flex-direction: ${isMobile ? 'column' : 'row'};
        `}
      >
        {/* Left Column */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              width: 100%;
            `}
          >
            <Text variant='captionLarge' weight='semiBold'>
              Team Info
            </Text>
            <div className='form-row'>
              <div className='form-field'>
                {!isCreatingTeam ? (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 16px;
                      width: 100%;
                    `}
                  >
                    <SelectInput
                      inputRef={teamRef}
                      onSelect={onSelect}
                      allowClear={true}
                      name='team'
                      label='Team *'
                      value={state.team}
                      onChange={(value) => {
                        if (value === 'create_new') {
                          setIsCreatingTeam(true)
                          setState((prev) => ({ ...prev, team: '' }))
                        } else {
                          setState((prev) => ({ ...prev, team: value }))
                        }
                      }}
                      fieldErrors={state.fieldErrors}
                      customError={state.customError}
                      options={teamOptions}
                      disabled={false}
                      extraCheckBox={true}
                      extraCheckBoxLabel='Search On Root Organization'
                      extraCheckBoxOnChange={(value) => {
                        setIsGlobal(value)
                      }}
                      showSearch={true}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSearch={(value) => setQuery(value)}
                    />
                    <div
                      css={css`
                        display: flex;
                        justify-content: flex-end;
                        align-self: flex-end;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <Button
                        text='New Team'
                        onClick={() => setIsCreatingTeam(true)}
                        variant='primary'
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <TextInput
                      name='new_team'
                      label='New Team Name *'
                      value={newTeamName}
                      setValue={setNewTeamName}
                      fieldErrors={state.fieldErrors}
                      customError={state.customError}
                    />
                    <div
                      css={css`
                        display: flex;
                        gap: 8px;
                        margin-top: 8px;
                        align-self: flex-end;
                      `}
                    >
                      <Button
                        text='Save'
                        onClick={onSaveTeam}
                        variant='primary'
                        disabled={!newTeamName}
                      />
                      <Button
                        text='Cancel'
                        onClick={onCancelTeam}
                        variant='secondary'
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              width: 100%;
            `}
          >
            <Text variant='captionLarge' weight='semiBold'>
              Team Officials Info *
            </Text>
            {!isAddingTeamOfficial ? (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                At least 1 team official should be selected as Treasurer
              </Text>
            ) : null}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
                width: 100%;
              `}
            >
              {!isAddingTeamOfficial ? (
                <>
                  <Table
                    pagination={false}
                    bordered={true}
                    dataSource={state.team_officials}
                    scroll={{ x: isMobile ? 500 : undefined }}
                    columns={[
                      {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'Role',
                        dataIndex: 'title',
                        key: 'title',
                        render: (title: string) => {
                          const option = titleOptions.find(
                            (opt) => String(opt.value) === String(title)
                          )
                          return option?.label || title
                        },
                      },
                      {
                        title: 'Treasurer',
                        dataIndex: 'is_treasurer',
                        key: 'is_treasurer',
                        width: '80px',
                        render: (is_treasurer: boolean) => (
                          <div
                            css={css`
                              text-align: center;
                            `}
                          >
                            {is_treasurer ? (
                              <CheckCircleOutlined
                                css={css`
                                  color: ${colors.SUCCESS_300};
                                  font-size: 20px;
                                `}
                              />
                            ) : null}
                          </div>
                        ),
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        width: '120px',
                        render: (_, record) => (
                          <div
                            css={css`
                              display: flex;
                              gap: 8px;
                            `}
                          >
                            <Tooltip
                              title='Edit'
                              placement='top'
                              zIndex={11000}
                            >
                              <EditOutlined
                                css={css`
                                  color: ${colors.PRIMARY_500};
                                  font-size: 20px;
                                `}
                                onClick={() => onEditTeamOfficial(record)}
                              />
                            </Tooltip>
                            <Tooltip
                              title='Delete'
                              placement='top'
                              zIndex={11000}
                            >
                              <DeleteOutlined
                                css={css`
                                  color: ${colors.ERROR_300};
                                  font-size: 20px;
                                `}
                                onClick={() => {
                                  setState((prev) => ({
                                    ...prev,
                                    team_officials: prev.team_officials.filter(
                                      (official) => official.id !== record.id
                                    ),
                                  }))
                                }}
                              />
                            </Tooltip>
                            <Tooltip
                              title={
                                record.is_treasurer
                                  ? 'Remove Treasurer'
                                  : 'Set as Treasurer'
                              }
                              placement='top'
                              zIndex={11000}
                            >
                              <DollarOutlined
                                css={css`
                                  color: ${record.is_treasurer
                                    ? colors.SUCCESS_300
                                    : colors.NEUTRAL_50};
                                  font-size: 20px;
                                `}
                                onClick={() => {
                                  setState((prev) => ({
                                    ...prev,
                                    team_officials: prev.team_officials.map(
                                      (official) => ({
                                        ...official,
                                        is_treasurer:
                                          official.id === record.id
                                            ? !official.is_treasurer
                                            : false,
                                      })
                                    ),
                                  }))
                                }}
                              />
                            </Tooltip>
                          </div>
                        ),
                      },
                    ]}
                    css={css`
                      display: ${state?.team_officials?.length > 0
                        ? 'block'
                        : 'none'};
                      .ant-table-thead > tr > th {
                        background-color: ${colors.SECONDARY_100};
                      }
                      .ant-table-tbody > tr > td {
                        background-color: ${colors.SECONDARY_200};
                      }
                      border-radius: 8px;
                    `}
                  />
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      align-self: flex-end;
                      width: ${isMobile ? '100%' : '50%'};
                    `}
                  >
                    <Button
                      text='New Team Official'
                      onClick={() => setIsAddingTeamOfficial(true)}
                      variant='primary'
                    />
                  </div>
                </>
              ) : (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                  `}
                >
                  <div className='form-row'>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <TextInput
                        name='team_official_first_name'
                        label='First Name *'
                        value={newTeamOfficial.firstName}
                        setValue={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            firstName: value,
                          }))
                        }
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <TextInput
                        name='team_official_last_name'
                        label='Last Name *'
                        value={newTeamOfficial.lastName}
                        setValue={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            lastName: value,
                          }))
                        }
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                      />
                    </div>
                  </div>
                  <div className='form-row'>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <TextInput
                        name='team_official_email'
                        label='Email *'
                        value={newTeamOfficial.email}
                        setValue={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            email: value,
                          }))
                        }
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                        type='email'
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <SelectInput
                        name='team_official_title'
                        label='Title'
                        value={newTeamOfficial.title}
                        onChange={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            title: value,
                          }))
                        }
                        options={titleOptions}
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                        disabled={false}
                      />
                    </div>
                  </div>
                  <div className='form-row'>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <PhoneInput
                        label='Phone Number'
                        value={newTeamOfficial.phone}
                        onChange={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            phone: value,
                          }))
                        }
                        disabled={false}
                        error={state.customError.team_official_phone}
                      />
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <SelectInput
                        name='team_official_country'
                        label='Country'
                        value={newTeamOfficial.country}
                        onChange={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            country: value,
                          }))
                        }
                        options={countryOptions}
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                        disabled={false}
                        showSearch={true}
                        filterOption={(input, option) =>
                          (option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onSearch={(value) => setQuery(value)}
                      />
                    </div>
                  </div>
                  <div className='form-row'>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                        width: ${isMobile ? '100%' : '50%'};
                      `}
                    >
                      <TextInput
                        name='team_official_zip_code'
                        label='Zip Code'
                        value={newTeamOfficial.zipCode}
                        setValue={(value) =>
                          setNewTeamOfficial((prev) => ({
                            ...prev,
                            zipCode: value,
                          }))
                        }
                        fieldErrors={state.fieldErrors}
                        customError={state.customError}
                      />
                    </div>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      gap: 8px;
                      margin-top: 16px;
                    `}
                  >
                    <Button
                      text='Cancel'
                      onClick={onCancel}
                      variant='secondary'
                    />
                    <Button text='Save' onClick={onSave} variant='primary' />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              width: 100%;
            `}
          >
            <Text variant='captionLarge' weight='semiBold'>
              Admin Options
            </Text>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  align-self: stretch;
                `}
              >
                <SwitchInput
                  title='Auto-Approval'
                  subtitle={
                    'Team is auto-approved. Deposit not required upfront.'
                  }
                  value={state.auto_approval}
                  onChange={(value) => {
                    setState((prev) => ({ ...prev, auto_approval: value }))
                  }}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  align-self: stretch;
                `}
              >
                <SwitchInput
                  title='Complimentary Registration'
                  subtitle={'Team is auto-approved. Add Team at no Cost.'}
                  value={state.complimentary}
                  onChange={(value) => {
                    setState((prev) => ({ ...prev, complimentary: value }))
                  }}
                />
              </div>
            </div>
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              width: 100%;
            `}
          >
            <Text variant='captionLarge' weight='semiBold'>
              Registration Info
            </Text>
            <div className='form-row'>
              <div className='form-field'>
                <Text variant='paragraphMedium'>
                  {' '}
                  <span
                    css={css`
                      text-decoration: ${state.coupon
                        ? 'line-through'
                        : 'none'};
                      color: ${state.coupon ? colors.NEUTRAL_50 : 'inherit'};
                    `}
                  >
                    Cost Per Team: {cost}
                  </span>
                </Text>
                {state.coupon && (
                  <Text variant='paragraphMedium'>
                    <span
                      css={css`
                        color: ${colors.SUCCESS_200};
                        white-space: nowrap;
                      `}
                    >
                      Price with Discount:{' '}
                      {state.coupon.discount_type === 'percentage'
                        ? formatCurrency(
                            costValue * (1 - state.coupon.discount / 100),
                            currency?.name || 'CAD'
                          )
                        : formatCurrency(
                            costValue - state.coupon.discount,
                            currency?.name || 'CAD'
                          )}
                    </span>
                  </Text>
                )}
                {singlePayment ? null : (
                  <div className='form-field'>
                    <Text variant='paragraphMedium'>Deposit: {deposit}</Text>
                  </div>
                )}
              </div>
            </div>
            <div className='form-row'>
              <div className='form-field'>
                <TextInput
                  name='coupon_code'
                  label='Coupon Code'
                  value={state.couponCode}
                  setValue={(value) =>
                    setState((prev) => ({ ...prev, couponCode: value }))
                  }
                  fieldErrors={state.fieldErrors}
                  customError={state.customError}
                  disabled={!!state.coupon}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: flex-end;
                `}
              >
                <Button
                  text={state.coupon ? 'Clear' : 'Validate'}
                  onClick={
                    state.coupon
                      ? () =>
                          setState((prev) => ({
                            ...prev,
                            coupon: null,
                            couponCode: '',
                          }))
                      : validateCouponCode
                  }
                  variant='secondary'
                  disabled={!state.couponCode && !state.coupon}
                />
              </div>
            </div>
            {state.coupon && (
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.SUCCESS_200}
              >
                Coupon applied:{' '}
                {state.coupon.discount_type === 'percentage'
                  ? `${state.coupon.discount}%`
                  : formatCurrency(
                      state.coupon.discount,
                      currency?.name || 'CAD'
                    )}{' '}
                off
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminTeamRegisterForm
